import PropTypes from "prop-types";
import { Row, Col } from "react-bootstrap";
import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";

import { GradientLineChart } from "shared";
import { Tooltip } from "components/Tooltip";
import { contains } from "utils";
import { getHpLineColors } from "features/Strategy/utils";
import { MONTH_DATE_YEAR_FORMAT } from "utils/date";
import { StrategyCardContainer } from "./StrategyCard.styles";
import StrategyControlModal from "shared/StrategyControl";
import { loggedInState } from "features/Auth/slice/selector";
import { useNavigation } from "utils/navigate";
import { Button } from "components/Button/index";

export const StrategyCard = ({
  data,
  showName = true,
  handleCardClick,
  type,
}) => {
  const {
    name,
    historical_performance = [],
    key_stats,
    strategy_image,
    strategy_label,
    description,
    author_name,
    strategy_label_color,
  } = data;

  const theme = useTheme();
  const { navigateToV2Signup } = useNavigation();
  const isLoggedInState = useSelector(loggedInState);

  const labelBgColor =
    theme.mode === "light"
      ? strategy_label_color?.bg
      : strategy_label_color?.dark_bg;

  const labelFontColor =
    theme.mode === "light"
      ? strategy_label_color?.fg
      : strategy_label_color?.dark_fg;

  const [showStrategyControlModal, setShowStrategyControlModal] =
    useState(false);

  const toggleStrategyControlModal = async () => {
    setShowStrategyControlModal(!showStrategyControlModal);
  };

  const hPerformance = useMemo(() => {
    if (historical_performance) {
      return historical_performance?.map((value) => {
        return {
          name: value.name,
          $: value.Profit || value["Strategy Value"],
        };
      });
    }
  }, [historical_performance]);

  const totalReturn = useMemo(() => {
    if (key_stats) {
      const totalRetuenDetails = key_stats.find(
        (state) => state.name === "Total Return"
      );
      return totalRetuenDetails?.value || 0;
    }
  }, [key_stats]);

  const handleInvestClick = () => {
    if (!isLoggedInState) {
      navigateToV2Signup();
    } else {
      toggleStrategyControlModal();
    }
  };

  return (
    <StrategyCardContainer data-strategy-name={name}>
      <div className="strategy-card-header" onClick={handleCardClick}>
        <Row className="top-header">
          <Col md={6} xs={6}>
            {strategy_image && <img src={strategy_image} />}
          </Col>
          {strategy_label && (
            <Col md={6} xs={6} className="text-end">
              <Button
                className="strategy-type"
                size="small"
                variant="secondary"
                style={{
                  backgroundColor: labelBgColor,
                  color: labelFontColor,
                }}
              >
                {strategy_label}
              </Button>
            </Col>
          )}
        </Row>

        {showName && (
          <Row className="name-header">
            <Col md={10} xs={10}>
              {name && name.length > 34 ? (
                <Tooltip value={name} placement="bottom">
                  <h6 className="strategy-name text-ellipsis">{name}</h6>
                </Tooltip>
              ) : (
                <h6 className="strategy-name text-ellipsis">{name}</h6>
              )}
              <div className="author-name">{author_name}</div>
            </Col>
          </Row>
        )}
      </div>

      <div className="description">
        <p>{description}</p>
      </div>

      <Row className="return-wrapper">
        <Col md={6} xs={6}>
          <div
            className={classNames("percentage", {
              gain: !contains(totalReturn, "-"),
              loss: contains(totalReturn, "-"),
            })}
          >
            {!contains(totalReturn, "-") > 0 && "+"} {totalReturn || 0}
          </div>
          <div className="total">Total Return</div>
        </Col>
        <Col md={6} xs={6}>
          <GradientLineChart
            title=""
            data={hPerformance}
            gridVertical
            fillOpacity={1}
            assetsColors={getHpLineColors(hPerformance, true)}
            timestamp={MONTH_DATE_YEAR_FORMAT}
            percentageVal={false}
            height={"45px"}
            showXAxis={false}
            showGridLines={false}
            areaProps={{ isAnimationActive: false, strokeWidth: 2 }}
            yAxisProps={{
              hide: true,
              domain: ["dataMin", "dataMax"],
            }}
          />
        </Col>
      </Row>
      {type == "suggested" ? (
        <Button
          size="large"
          className="btn-subscribed"
          onClick={handleCardClick}
        >
          View details
        </Button>
      ) : (
        <Button
          size="large"
          className="btn-subscribed"
          onClick={handleInvestClick}
        >
          Invest
        </Button>
      )}

      <StrategyControlModal
        show={showStrategyControlModal}
        onHide={toggleStrategyControlModal}
        selectedStrategyData={data}
      />
    </StrategyCardContainer>
  );
};

const DataPropTypes = PropTypes.shape({
  strategy_id: PropTypes.string,
  name: PropTypes.string,
  key_stats: PropTypes.array,
  historical_performance: PropTypes.array,
  author_name: PropTypes.string,
  info: PropTypes.string,
  current_price: PropTypes.number,
  has_license: PropTypes.bool,
  strategy_image: PropTypes.string,
  strategy_label: PropTypes.string,
  description: PropTypes.string,
  strategy_label_color: PropTypes.object,
});

StrategyCard.propTypes = {
  data: DataPropTypes,
  showPrice: PropTypes.bool,
  showName: PropTypes.bool,
  handleCardClick: PropTypes.func,
  isAutomatedInvesting: PropTypes.bool,
  handleInvest: PropTypes.func,
  type: PropTypes.string,
};
